import {Observable} from 'rxjs';

/**
 * Defines a simple storage service contract.
 * @see FrontLocalStorageService
 */
export abstract class FrontStorageService {
    /**
     * Implementation should provide a prefix key to avoid collision. This can be seen as a namespace.
     * For instance, if your application can be used by several third parties (i.e. one domain can handle several third parties)
     * then you should provide a <code>prefixKey</code> based on the third party ID.
     *
     * Otherwise, your data could be lost or, even worse, in inconsistent state when you switch third parties.
     *
     * @private
     */
    private readonly prefixKey: string;

    protected constructor(prefixKey: string) {
        this.prefixKey = prefixKey;
    }

    public abstract set<T>(key: string, value: T): Observable<T>;

    public abstract remove(key: string): Observable<void>;

    /**
     *
     * @param key Item key
     * @return Observable<T> containing the key's value or undefined if the key does not exist
     */
    public abstract get<T>(key: string): Observable<T | undefined>;

    /**
     * Add prefix to key. This method should be called before every interaction with the storage.
     *
     * @param key Key used to store data
     * @see {@link prefixKey}
     */
    protected prepareKey(key: string): string {
        return `${this.prefixKey}_${key}`;
    }
}
